import { CartUpdateEvent } from '@theme/events';

/**
 * Keep diamond addon lines linked 1:1 with their parent ring.
 * Each addon is its own cart line (unique `_lineUid` / `_parentKey` properties).
 * Tracking properties use `_` prefix → hidden in cart drawer + checkout.
 */

const SYNC_HEADER = 'X-Diamond-Cart-Sync';
const MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const CHANGE_RE = /\/cart\/(change)(\.js)?(\?|$)/;

let syncing = false;
/** @type {any | null} */
let cartCache = null;

/**
 * @param {boolean} [force]
 */
async function getCart(force = false) {
  if (!force && cartCache) return cartCache;
  cartCache = await fetch(Theme.routes.cart_url + '.js').then((r) => r.json());
  return cartCache;
}

function clearCartCache() {
  cartCache = null;
}

function cartUpdateUrl() {
  const base = Theme.routes.cart_update_url || '/cart/update';
  return base.endsWith('.js') ? base : `${base}.js`;
}

/**
 * @param {HeadersInit | undefined} headers
 */
function hasSyncHeader(headers) {
  if (!headers) return false;
  if (headers instanceof Headers) return headers.has(SYNC_HEADER);
  if (Array.isArray(headers)) return headers.some(([k]) => k.toLowerCase() === SYNC_HEADER.toLowerCase());
  const asRecord = /** @type {Record<string, string>} */ (headers);
  return Boolean(asRecord[SYNC_HEADER] || asRecord[SYNC_HEADER.toLowerCase()]);
}

/**
 * @param {Record<string, number>} updates
 * @param {{ sections?: string, sections_url?: string }} [extra]
 */
async function postUpdates(updates, extra = {}) {
  return fetch(cartUpdateUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      [SYNC_HEADER]: '1',
    },
    body: JSON.stringify({
      updates,
      ...extra,
    }),
  });
}

/**
 * Remove orphan addon lines, and re-add a missing priced addon for each parent.
 */
async function syncDiamondLinks() {
  if (syncing) return;
  syncing = true;

  try {
    const cart = await getCart(true);
    const parents = cart.items.filter((item) => item.properties?._itemKey);
    const parentKeys = new Set(parents.map((item) => item.properties._itemKey));
    const addonsByParent = new Map(
      cart.items
        .filter((item) => item.properties?._parentKey)
        .map((item) => [item.properties._parentKey, item])
    );

    /** @type {Record<string, number>} */
    const updates = {};
    for (const item of cart.items) {
      const parentKey = item.properties?._parentKey;
      if (parentKey && !parentKeys.has(parentKey)) {
        updates[item.key] = 0;
      }
    }

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    if (Object.keys(updates).length) {
      const response = await postUpdates(updates, {
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });
      const json = await response.json();
      clearCartCache();
      document.dispatchEvent(
        new CartUpdateEvent(json, 'additional-diamonds-cart', {
          source: 'additional-diamonds-cart',
          itemCount: json.item_count,
          sections: json.sections,
        })
      );
      return;
    }

    /** @type {Array<{id:number, quantity:number, properties: Record<string,string>}>} */
    const missingAddons = [];
    for (const parent of parents) {
      const itemKey = parent.properties._itemKey;
      const addOnVariant = parent.properties._addOnVariant;
      if (!itemKey || !addOnVariant || addonsByParent.has(itemKey)) continue;

      const variantId = Number(String(addOnVariant).split(':')[0]);
      if (!variantId) continue;

      /** @type {Record<string, string>} */
      const props = {
        _parentKey: itemKey,
        _lineUid: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`,
      };
      if (parent.product_title) props._parentTitle = parent.product_title;
      if (parent.variant_title) props._parentTitle = `${parent.product_title} - ${parent.variant_title}`;

      missingAddons.push({
        id: variantId,
        quantity: 1,
        properties: props,
      });
    }

    if (!missingAddons.length) return;

    const addUrl = Theme.routes.cart_add_url || '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [SYNC_HEADER]: '1',
      },
      body: JSON.stringify({
        items: missingAddons,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const json = await response.json();
    clearCartCache();

    document.dispatchEvent(
      new CartUpdateEvent(json, 'additional-diamonds-cart', {
        source: 'additional-diamonds-cart',
        itemCount: json.item_count,
        sections: json.sections,
      })
    );
  } catch (error) {
    console.error(error);
  } finally {
    syncing = false;
  }
}

/**
 * @param {any} body
 * @param {any} cart
 */
function findCartItem(body, cart) {
  if (body?.id != null) {
    const id = String(body.id);
    return cart.items.find((item) => item.key === id || String(item.variant_id) === id) || null;
  }
  if (body?.line != null) {
    return cart.items[Number(body.line) - 1] || null;
  }
  return null;
}

const nativeFetch = window.fetch.bind(window);

window.fetch = async function diamondCartFetch(input, init = {}) {
  const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
  const headers = init.headers;
  const isOurSync = hasSyncHeader(headers);
  const isChange = CHANGE_RE.test(url);
  const isMutation = MUTATION_RE.test(url);

  if (isChange && init.body && !isOurSync) {
    try {
      const rawBody = typeof init.body === 'string' ? init.body : null;
      if (rawBody) {
        const body = JSON.parse(rawBody);
        const cart = await getCart(true);
        const item = findCartItem(body, cart);

        // Addon lines are display-only: block qty/remove mutations.
        if (item?.properties?._parentKey) {
          return postUpdates(
            { [item.key]: item.quantity },
            {
              sections: body.sections,
              sections_url: body.sections_url,
            }
          );
        }

        if (Number(body.quantity) === 0 && item?.properties?._itemKey) {
          const addon = cart.items.find((line) => line.properties?._parentKey === item.properties._itemKey);

          if (addon) {
            // Remove only this parent's linked addon line (separate line item, qty 1).
            const response = await postUpdates(
              {
                [item.key]: 0,
                [addon.key]: 0,
              },
              {
                sections: body.sections,
                sections_url: body.sections_url,
              }
            );
            clearCartCache();
            return response;
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const response = await nativeFetch(input, init);

  if (isMutation && !isOurSync && response.ok) {
    clearCartCache();
    response
      .clone()
      .json()
      .then(() => syncDiamondLinks())
      .catch(() => {});
  }

  return response;
};

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const removeButton = target.closest('.cart-items__remove');
    if (!removeButton) return;

    const row = removeButton.closest('tr[data-key]');
    if (!(row instanceof HTMLElement)) return;
    const itemKey = row.dataset.diamondItemKey;
    if (!itemKey) return;

    document.querySelectorAll(`tr[data-diamond-parent-key="${CSS.escape(itemKey)}"]`).forEach((addonRow) => {
      if (!(addonRow instanceof HTMLElement)) return;
      addonRow.style.setProperty('--row-height', `${addonRow.clientHeight}px`);
      addonRow.classList.add('removing');
    });
  },
  true
);
